import { MemberRoleTypes, SdbMemberTypes } from "@custom-types/member-types";
import { SdbTeamMemberBase, TeamsSortBy } from "@custom-types/teams-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { neutral, purple } from "@flat-ui";
import { getHigherRole } from "@utils/member-utils";
import { getPrettyName } from "@utils/user-utils";
import { GridSortModel } from "@mui/x-data-grid-pro";
import { getTeamsSortBy } from "@utils/team-utils";
import { convertGridSortModelToSortBy } from "@utils/sort-utils";
import { DEFAULT_TEAMS_SORT_BY } from "@components/teams/teams-constants";
import { isTeamsSortField } from "@custom-types/type-guards";

/**
 * Maps team members to sdb members by calculating the higher role of the member
 * based on the team role and company role if companyMembersMap is provided
 */
export function mapTeamMembersToSdbMembers(
  members: FaroCoreApiTypes.TeamMember[],
  teamRole: MemberRoleTypes,
  companyMembers?: Map<string, SdbMemberTypes>
): SdbTeamMemberBase[] {
  return members.map((member) => {
    const companyRole = companyMembers?.get(member.userResponse.identity)?.role;
    return {
      ...member.userResponse,
      dateJoined: member.addedAt,
      name: getPrettyName(member.userResponse),
      role: companyRole ? getHigherRole(teamRole, companyRole) : teamRole,
    };
  });
}

/** Properties for the chip icon shown for auto-managed teams. */
type TeamCategoryChipColors = {
  /** The border color for the chip team */
  border: string;

  /** The background color for the chip team */
  background: string;

  /** The icon color for the chip team */
  icon: string;
};

interface TeamDisplayProperties {
  /** Indicates if the team is auto-managed */
  isAutoManaged: boolean;

  /** The color used for the team category. This is the background color for the chip */
  color: string;

  /**
   * The label used for the team category.
   * This is displayed instead of the action buttons for managed teams.
   */
  label: string | null;

  /**
   * The description used for the tooltip.
   * Only auto-managed teams show this description on the chip.
   * Has nothing to do with the description for the team stored in the backend.
   */
  description: string | null;

  /**
   * The properties for the chip icon shown for auto-managed teams.
   * This is a small chip on the left side of the action to view details.
   */
  chipProps: TeamCategoryChipColors | null;
}

const AUTO_MANAGED_TEAM_COLOR = purple[400];
const AUTO_MANAGED_TEAM_LABEL = "Auto-managed";
const AUTO_MANAGED_TEAM_CHIP_PROPS: TeamDisplayProperties["chipProps"] = {
  border: purple[100],
  background: purple[50],
  icon: purple[400],
};

export const teamDisplayProperties: {
  [kind in FaroCoreApiTypes.TeamCategoryKind]: TeamDisplayProperties;
} = {
  ["email-domains"]: {
    isAutoManaged: true,
    color: AUTO_MANAGED_TEAM_COLOR,
    label: AUTO_MANAGED_TEAM_LABEL,
    description:
      "Users who log in via the configured SSO connection are automatically invited to this team.",
    chipProps: AUTO_MANAGED_TEAM_CHIP_PROPS,
  },
  ["all-members"]: {
    isAutoManaged: true,
    color: AUTO_MANAGED_TEAM_COLOR,
    label: AUTO_MANAGED_TEAM_LABEL,
    description:
      "All members invited to the workspace are automatically invited to this team.",
    chipProps: AUTO_MANAGED_TEAM_CHIP_PROPS,
  },
  ["default"]: {
    isAutoManaged: false,
    color: neutral[700],
    label: null,
    description: null,
    chipProps: null,
  },
};

/**
 * @returns a valid teams `sortBy` option from the passed GridSortModel
 * @param gridSortModel Teams table sort model
 */
export function getSortByFromGridSortModel(
  gridSortModel: GridSortModel
): TeamsSortBy {
  return convertGridSortModelToSortBy(gridSortModel, {
    defaultSortBy: DEFAULT_TEAMS_SORT_BY,
    isValidSortField: isTeamsSortField,
    getSortBy: getTeamsSortBy,
  });
}
